import { useCurrentPayload } from '@anm/components/drag-drop/DropZone';
import Image from '@anm/components/image/Image';
import cutFileExtension from '@anm/helpers/cutFileExtension';
import isAllowedExtension from '@anm/helpers/is/isAllowedExtension';
import useSwitchState from '@anm/hooks/useSwitchState';
import { HEADER_HEIGHT } from '@anm/styles/constants';
import { useCallback, useEffect, useRef, useState, DragEvent, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { projectActions, projectSelectors } from '../../../store/modules/project';
import vtConfig from '../../../config';
import useMyProjectsEmptyList from '../VideoFolders/hooks/useMyProjectsEmptyList';

import { DropZoneBG, DropZoneText } from './Wrapper';
import { appNotificationActions } from '@anm/store/modules/appNotifications';
import { uploadsActions } from '@anm/store/modules/uploads';
import { userSelectors } from '../../../store/modules/user';
import useProgressListener from '@anm/store/modules/uploads/hooks/useProgressListener';

const {
  supportedVideoExtensions,
  uploadVideoDefaults: { metaInfo }
} = vtConfig();

const allowedExtensionsList = supportedVideoExtensions.map(e => `.${e}`).join();

export const useFocusField = () => {
  const [canFocusField, setCanFocusField] = useState(false);

  const handleCreateNewFolder = useCallback(() => setCanFocusField(true), []);

  return [canFocusField, setCanFocusField, handleCreateNewFolder] as const;
};

export const useUploadFileByDragAndDrop = () => {
  const dispatch = useDispatch();
  const [isFolderDragOver, setIsFolderDragOver] = useState(false);
  const [isDropZoneDragOver, setIsDropZoneDragOver] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const { payload: folderProjectPayload } = useCurrentPayload();
  const user = useSelector(userSelectors.selectUser);

  const listener = useProgressListener();

  const handleDropFile = useCallback((evt: DragEvent<HTMLDivElement>) => {
    evt.preventDefault();
    evt.stopPropagation();

    const file = evt.dataTransfer.files[0];
    setIsDropZoneDragOver(false);
    setIsFolderDragOver(false);

    if (!file) return;

    if (!isAllowedExtension(file.name, allowedExtensionsList)) {
      dispatch(
        appNotificationActions.notify({
          type: 'error',
          title: 'This file type is not supported'
        })
      );
      return;
    }

    const name = cutFileExtension(file.name);
    const description = `${name} - ${metaInfo.description}`;

    dispatch(
      uploadsActions.createUpload({
        user: user.profile,
        isUploadFile: true,
        createHost: true,
        uploadType: 'new',
        progressListener: listener,
        uploads: [
          {
            file,
            meta: { name, description }
          }
        ]
      })
    );
  }, []);

  const handleDragOver = useCallback(
    (evt: DragEvent<HTMLDivElement>) => {
      evt.preventDefault();
      evt.stopPropagation();

      if (!folderProjectPayload) {
        setIsDropZoneDragOver(true);
      }
    },
    [folderProjectPayload]
  );

  const handleDragLeave = useCallback((evt: DragEvent<HTMLDivElement>) => {
    evt.preventDefault();

    const el = evt.relatedTarget as Element;

    if (!el) return;

    const folderName = el.getAttribute('data-attribute-foldername');

    const canCleanState =
      folderName ||
      el.id === 'videos-content' ||
      el.id === 'left-bar-content' ||
      el.id === 'folder-preview' ||
      el.id === 'left-navbar';

    if (canCleanState) {
      setIsFolderDragOver(false);
      setIsDropZoneDragOver(false);
    }
  }, []);

  return [
    dropZoneRef,
    isFolderDragOver,
    isDropZoneDragOver,
    handleDropFile,
    handleDragOver,
    handleDragLeave,
    setIsFolderDragOver,
    setIsDropZoneDragOver
  ] as const;
};

const SCROLL_Y = 71;
const DROP_ZONE_AREA_MARGIN_BOTTOM = 40;
const headerHeightNum = +HEADER_HEIGHT.split('px')[0];
const topOnTopPage = 134;
const leftBarWidth = 182;
const contentHorizontalPadding = 40 * 2;

export const useDropZoneBG = (isDropZoneDragOver: boolean) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [pageScrolled, setPageScrolled] = useState(0);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [isEmptyList] = useMyProjectsEmptyList();

  const topAfterScroll = topOnTopPage + pageScrolled - SCROLL_Y;
  const top = pageScrolled <= SCROLL_Y ? topOnTopPage : topAfterScroll;

  useEffect(() => {
    if (!contentRef.current) return;

    const newWidth = window.innerWidth - (leftBarWidth + contentHorizontalPadding);

    setWidth(newWidth);
  }, [contentRef.current, isDropZoneDragOver]);

  useEffect(() => {
    setPageScrolled(window.pageYOffset);

    const breadcrumbsHeight = 24 + 60 + 30;
    const topGaps = breadcrumbsHeight + headerHeightNum;

    const scrollingCompensation = window.scrollY > topGaps ? window.scrollY + 60 : topGaps;

    const newHeight = isEmptyList
      ? window.innerHeight - scrollingCompensation - DROP_ZONE_AREA_MARGIN_BOTTOM
      : window.innerHeight - topGaps - DROP_ZONE_AREA_MARGIN_BOTTOM;

    setHeight(newHeight);
  }, [isDropZoneDragOver, top, isEmptyList, contentRef.current]);

  const DropZoneBGContent: FC = () => (
    <DropZoneBG left={21} {...{ top, width, height }}>
      {isEmptyList && (
        <>
          <Image src="images/icons/ic_upload.svg" alt="upload video" />
          <DropZoneText>Drop files here to upload</DropZoneText>
        </>
      )}
    </DropZoneBG>
  );

  return [contentRef, DropZoneBGContent] as const;
};

export const useRemovedProjectError = () => {
  const [canShow, show] = useSwitchState();

  const projectError = useSelector(projectSelectors.selectCurrentProjectError);

  const dispatch = useDispatch();

  useEffect(() => {
    const isRemoved = projectError?.code === 404;
    if (!isRemoved) return;

    show();

    return () => {
      projectError && dispatch(projectActions.clearCurrentProject());
    };
  }, [projectError]);

  return canShow;
};
